/** @module components/hot-spot-quiz */


import Model from '@lernetz/ts-lib/src/core/model/Model';
import View from '@lernetz/ts-lib/src/core/view/View';
import Repeat from '@lernetz/ts-lib/src/components/Repeat';

import {HotSpotArea} from './HotSpotArea';
import {Point} from './Point';
import {Spot} from './Spot';

import {CanShowCorrectness} from '../quiz/CanShowCorrectness';

import {RectangularHotSpotArea as template} from '../../templates/hot-spot-quiz';


export interface RectangularHotSpotAreaInitialState {

    /**
     * Sets the top-left corner of the rectangle.
     */
    topLeft: Point;

    /**
     * Sets the spots that are initially within this area.
     * Defaults to the empty array `[]`.
     */
    containedSpots?: Spot[];

    /**
     * Sets the bottom-right corner of the rectangle.
     */
    bottomRight: Point;

    /**
     * Sets whether the correctness of this hot spot area should be initially displayed.
     * Defaults to `false`.
     */
    showCorrectness?: boolean;
}


/**
 * A rectangular hot-spot area in a [[HotSpotQuiz]].
 *
 * @see [[HotSpotArea]], the main interface implemented by this class.
 */
export class RectangularHotSpotArea extends Model implements HotSpotArea, CanShowCorrectness {

    /**
     * Initializes a new instance of the [[RectangularHotSpotArea]] class.
     *
     * @param initialState The initial state for the new instance.
     * @param data An optional object to be passed through to the base class' ([[Model]]) constructor.
     */
    constructor(initialState: RectangularHotSpotAreaInitialState, data: object = {}) {
        super({
            bottomRight: { x: 1, y: 1 },
            containedSpots: [],
            showCorrectness: false,
            topLeft: { x: 0, y: 0 },
            ...initialState,
            ...data
        });
    }

    /**
     * Gets the top-left corner of the rectangle.
     */
    public readonly topLeft: Point;

    /** @inheritdoc */
    public readonly containedSpots: Spot[];

    /**
     * Gets the bottom-right corner of the rectangle.
     */
    public readonly bottomRight: Point;

    /**
     * Gets or sets whether the correctness of this hot spot area should be initially displayed.
     */
    public showCorrectness: boolean;

    /** @inheritdoc */
    public updateContainedSpots(spots: Point[]): void {
        const containedSpots = spots.filter(s => ((s.x >= this.topLeft.x && s.x <= this.bottomRight.x)
                                        && (s.y >= this.topLeft.y && s.y <= this.bottomRight.y)));
        this.set('containedSpots', containedSpots);
    }
}


export class RectangularHotSpotAreaView extends View<RectangularHotSpotArea> {

    public constructor() {
        super({
            template,
            registrations: {
                'repeat': Repeat,
            },
         });
    }

    init(): void {
        this.updateStyle();
        this.data.change.filter(({ name }) => name === 'showCorrectness').subscribe(this.updateStyle.bind(this));
    }

    private updateStyle(): void {
        const node = <HTMLElement> this.node;
        if (this.data.showCorrectness) {
            node.style.top = `${this.data.topLeft.y * 100}%`;
            node.style.left = `${this.data.topLeft.x * 100}%`;
            node.style.bottom = `${100 - this.data.bottomRight.y * 100}%`;
            node.style.right = `${100 - this.data.bottomRight.x * 100}%`;
        }
        else {
            node.style.top = '';
            node.style.left = '';
            node.style.bottom = '';
            node.style.right = '';
        }
    }
}
